/**
 * Email templates for Bunker.AI contact form
 * Supports multiple languages for international users
 */

export interface EmailTemplate {
  subject: string;
  body: string;
}

/**
 * Language-specific email templates for Bunker.AI inquiries
 */
export const emailTemplates: Record<string, EmailTemplate> = {
  de: {
    subject: 'Anfrage zu Bunker.AI',
    body: `Hallo liebes Bunker.AI-Team,

ich interessiere mich für Ihre Bunker.AI-Lösung und würde gern mehr darüber erfahren. Bitte senden Sie mir weitere Informationen oder kontaktieren Sie mich dazu persönlich.

Hier ein paar Angaben, die Ihnen helfen könnten, meine Anfrage besser einzuordnen:

Name / Firma:
E-Mail / Telefon:
Voraussichtliche Anzahl an Nutzern: (z. B. 5, 20 oder mehr)
Geplante Einsatzbereiche: (z. B. Dokumentenanalyse, Chatbot, Datenverarbeitung, etc.)
Art des Unternehmens: (z. B. Dienstleistung, Produktion, Handel, etc.)
Aktuell genutzte Softwarelösungen: (z. B. ERP, CRM, Cloud-Dienste, etc.)

Optional:
Anzahl der Mitarbeitenden:
Bereits Erfahrung mit KI-Lösungen?
Wann wäre ein guter Zeitpunkt für eine Demo?
Weitere Anmerkungen oder Wünsche:

Vielen Dank im Voraus für Ihre Rückmeldung!

Mit freundlichen Grüßen
[Ihr Name]`,
  },
  en: {
    subject: 'Inquiry about Bunker.AI',
    body: `Hello dear Bunker.AI Team,

I am interested in your Bunker.AI solution and would like to learn more. Please send me more information or contact me personally.

Here are some details that might help you better categorize my inquiry:

Name / Company:
Email / Phone:
Expected number of users: (e.g. 5, 20 or more)
Planned areas of use: (e.g. document analysis, chatbot, data processing, etc.)
Type of company: (e.g. service, manufacturing, retail, etc.)
Currently used software solutions: (e.g. ERP, CRM, cloud services, etc.)

Optional:
Number of employees:
Previous experience with AI solutions?
When would be a good time for a demo?
Further comments or requests:

Thank you in advance for your response!

Best regards
[Your Name]`,
  },
  fr: {
    subject: 'Demande concernant Bunker.AI',
    body: `Bonjour cher équipe Bunker.AI,

Je m'intéresse à votre solution Bunker.AI et souhaiterais en savoir plus. Veuillez m'envoyer plus d'informations ou me contacter personnellement.

Voici quelques détails qui pourraient vous aider à mieux classer ma demande :

Nom / Entreprise :
E-mail / Téléphone :
Nombre prévu d'utilisateurs : (ex. 5, 20 ou plus)
Domaines d'utilisation prévus : (ex. analyse de documents, chatbot, traitement de données, etc.)
Type d'entreprise : (ex. services, fabrication, commerce, etc.)
Solutions logicielles actuellement utilisées : (ex. ERP, CRM, services cloud, etc.)

Optionnel :
Nombre d'employés :
Expérience précédente avec les solutions d'IA ?
Quand serait un bon moment pour une démo ?
Autres commentaires ou demandes :

Merci d'avance pour votre réponse !

Cordialement
[Votre nom]`,
  },
  es: {
    subject: 'Consulta sobre Bunker.AI',
    body: `Hola estimado equipo de Bunker.AI,

Estoy interesado en su solución Bunker.AI y me gustaría saber más. Por favor, envíeme más información o contácteme personalmente.

Aquí hay algunos detalles que podrían ayudarle a categorizar mejor mi consulta:

Nombre / Empresa:
Correo electrónico / Teléfono:
Número previsto de usuarios: (ej. 5, 20 o más)
Áreas de uso planificadas: (ej. análisis de documentos, chatbot, procesamiento de datos, etc.)
Tipo de empresa: (ej. servicios, manufactura, comercio, etc.)
Soluciones de software actualmente utilizadas: (ej. ERP, CRM, servicios en la nube, etc.)

Opcional:
Número de empleados:
Experiencia previa con soluciones de IA?
¿Cuándo sería un buen momento para una demostración?
Comentarios o solicitudes adicionales:

¡Gracias de antemano por su respuesta!

Saludos cordiales
[Su nombre]`,
  },
  pt: {
    subject: 'Consulta sobre Bunker.AI',
    body: `Olá estimada equipe Bunker.AI,

Estou interessado na sua solução Bunker.AI e gostaria de saber mais. Por favor, envie-me mais informações ou entre em contato pessoalmente.

Aqui estão alguns detalhes que podem ajudá-lo a categorizar melhor minha consulta:

Nome / Empresa:
E-mail / Telefone:
Número previsto de usuários: (ex. 5, 20 ou mais)
Áreas de uso planejadas: (ex. análise de documentos, chatbot, processamento de dados, etc.)
Tipo de empresa: (ex. serviços, manufatura, comércio, etc.)
Soluções de software atualmente utilizadas: (ex. ERP, CRM, serviços de nuvem, etc.)

Opcional:
Número de funcionários:
Experiência prévia com soluções de IA?
Quando seria um bom momento para uma demonstração?
Comentários ou solicitações adicionais:

Obrigado antecipadamente pela sua resposta!

Atenciosamente
[Seu nome]`,
  },
  hi: {
    subject: 'बंकर.एआई के बारे में पूछताछ',
    body: `नमस्ते प्रिय बंकर.एआई टीम,

मुझे आपकी बंकर.एआई समाधान में रुचि है और मैं इसके बारे में अधिक जानना चाहूंगा। कृपया मुझे अधिक जानकारी भेजें या व्यक्तिगत रूप से संपर्क करें।

यहां कुछ विवरण दिए गए हैं जो आपको मेरी पूछताछ को बेहतर ढंग से वर्गीकृत करने में मदद कर सकते हैं:

नाम / कंपनी:
ईमेल / फोन:
अपेक्षित उपयोगकर्ताओं की संख्या: (जैसे 5, 20 या अधिक)
योजनाबद्ध उपयोग के क्षेत्र: (जैसे दस्तावेज़ विश्लेषण, चैटबॉट, डेटा प्रसंस्करण, आदि)
कंपनी का प्रकार: (जैसे सेवाएं, विनिर्माण, खुदरा, आदि)
वर्तमान में उपयोग की जाने वाली सॉफ्टवेयर समाधान: (जैसे ERP, CRM, क्लाउड सेवाएं, आदि)

वैकल्पिक:
कर्मचारियों की संख्या:
एआई समाधानों के साथ पिछला अनुभव?
डेमो के लिए एक अच्छा समय कब होगा?
अतिरिक्त टिप्पणियां या अनुरोध:

आपकी प्रतिक्रिया के लिए धन्यवाद!

विनम्र शुभकामनाएं
[आपका नाम]`,
  },
  zh: {
    subject: '关于 Bunker.AI 的咨询',
    body: `您好亲爱的 Bunker.AI 团队，

我对您的 Bunker.AI 解决方案感兴趣，想了解更多信息。请发送更多信息给我或亲自联系我。

以下是一些可能帮助您更好地分类我的咨询的详细信息：

姓名 / 公司：
电子邮件 / 电话：
预计用户数量：（例如 5、20 或更多）
计划使用领域：（例如文档分析、聊天机器人、数据处理等）
公司类型：（例如服务、制造、零售等）
当前使用的软件解决方案：（例如 ERP、CRM、云服务等）

可选：
员工人数：
以前使用过 AI 解决方案的经验？
什么时候是演示的好时机？
其他评论或请求：

提前感谢您的回复！

此致
[您的姓名]`,
  },
};

/**
 * Generate a mailto link with pre-filled contact form template
 * @param lang - Language code (e.g., 'de', 'en', 'fr', etc.)
 * @returns Complete mailto URL with encoded subject and body
 */
export function generateContactEmailLink(lang: string): string {
  const email = 'bunker@securesight.ai';
  const template = emailTemplates[lang] || emailTemplates.en; // Fallback to English

  const subject = encodeURIComponent(template.subject);
  const body = encodeURIComponent(template.body);

  return `mailto:${email}?subject=${subject}&body=${body}`;
}
